import React, { useMemo } from "react";
import { NormalSizes } from "../../../utils/prop-types";
import AvatarGroup from "./avatar-group";

interface Props {
  src?: string;
  stacked?: boolean;
  text?: string;
  size?: NormalSizes;
  isSquare?: boolean;
  className?: string;
  storageLink: boolean;
}

const defaultProps = {
  stacked: false,
  text: "",
  size: "small" as NormalSizes,
  isSquare: false,
  className: "",
  storageLink: false,
};

type NativeAttrs = Omit<
  Partial<React.ImgHTMLAttributes<any> & React.HTMLAttributes<any>>,
  keyof Props
>;
export type AvatarProps = Props & typeof defaultProps & NativeAttrs;

const getSize = (size: NormalSizes): string => {
  const sizes: { [key in NormalSizes]: string } = {
    mini: "w-6 h-6",
    small: "w-10 h-10",
    medium: "w-14 h-14",
    large: "w-20 h-20",
  };
  return sizes[size];
};

const safeText = (text: string): string => {
  if (text.length <= 4) return text;
  return text.slice(0, 3);
};

const Avatar: React.FC<AvatarProps> = ({
  src,
  stacked,
  text,
  size,
  isSquare,
  className,
  storageLink,
  ...props
}) => {
  const showText = !src;
  const sizeClass = useMemo(() => getSize(size), [size]);
  const link = storageLink
    ? `https://storage.googleapis.com/ghoststudio-public/${
        src || "no-image.svg"
      }`
    : src;
  return (
    <span
      className={`avatar ${sizeClass} inline-block relative overflow-hidden border border-gray-100 align-top ${
        !showText ? "bg-transparent" : "bg-primary"
      } ${stacked ? "-ml-3" : ""} ${
        isSquare ? "rounded-md" : "rounded-full"
      } ${className}`}
    >
      {!showText && (
        <img
          className={`avatar-img inline-block object-cover w-full h-full ${
            isSquare ? "rounded-md" : "rounded-full"
          }`}
          src={link ?? "no-image.svg"}
          {...props}
        />
      )}
      {showText && (
        <span
          className="avatar-text absolute left-1/2 top-1/2 text-sm text-center transform -translate-x-1/2 -translate-y-1/2 scale-75 whitespace-nowrap select-none text-white"
          {...props}
        >
          {safeText(text)}
        </span>
      )}
    </span>
  );
};

type MemoAvatarComponent<P = {}> = React.NamedExoticComponent<P> & {
  Group: typeof AvatarGroup;
};

type ComponentProps = Partial<typeof defaultProps> &
  Omit<Props, keyof typeof defaultProps> &
  NativeAttrs;

Avatar.defaultProps = defaultProps;

export default React.memo(Avatar) as MemoAvatarComponent<ComponentProps>;
